// DealEditDialog — диалог редактирования сделки (R34 / U-12).
// PUT /api/deals/{id} по контракту backend DealUpdateRequest:
// title/amount/currency/contactId/companyId/dueDate/description + expectedUpdatedAt
// (optimistic lock — берётся из deal.updatedAt на момент открытия).
// PATCH-semantics backend: null → поле не меняется (очистка контакта/компании недоступна).
// 409 (version conflict) → Alert внутри диалога с message от сервера, диалог НЕ закрывается
// (канон R24 / D24).
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  MenuItem,
  Button,
  Alert,
} from '@mui/material';
import { DatePickerField } from '@/components/forms';
import { dealsApi, type Deal, type DealCurrency } from '@/api/deals';
import { CURRENCIES_LOAD_ERROR, useActiveCurrencies } from '@/api/currencies';
import { useContactsPage, useContact, contactDisplayName } from '@/api/contacts';
import { useCompanyMap } from '@/api/companies';
import { extractApiErrorMessage } from '@/lib/apiError';
import { normalizeAmountInput, parseAmount, amountSchema } from '@/lib/money';
import { t } from '@/i18n';

/** Zod-схема формы редактирования сделки (зеркало backend-валидации DealUpdateRequest). */
const dealEditSchema = z.object({
  title: z.string().min(1, 'Название обязательно').max(255, 'Слишком длинное название'),
  // U-32: единый модуль money.ts — замена z.coerce.number() ('' → 0 молча):
  // пусто → «Укажите сумму», не число → «Укажите сумму числом», <= 0 → «больше 0».
  amount: amountSchema,
  currency: z.string().length(3),
  companyId: z.string().optional().or(z.literal('')),
  contactId: z.string().optional().or(z.literal('')),
  dueDate: z.string().optional().or(z.literal('')),
  description: z.string().max(4000, 'Слишком длинное описание').optional().or(z.literal('')),
});

type DealEditFormValues = z.infer<typeof dealEditSchema>;

interface DealEditDialogProps {
  /** Редактируемая сделка (источник дефолтов формы и expectedUpdatedAt). */
  deal: Deal;
  /** Флаг открытия dialog. */
  open: boolean;
  /** Колбэк закрытия (отмена или успешный submit). */
  onClose: () => void;
}

/** Диалог редактирования полей сделки (U-12). Монтируется заново при каждом открытии. */
export function DealEditDialog({ deal, open, onClose }: DealEditDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const catalogQuery = useActiveCurrencies();
  const currencies = catalogQuery.data ?? [];
  const companyMap = useCompanyMap();
  const contactsPage = useContactsPage();

  const { control, handleSubmit, watch } = useForm<DealEditFormValues>({
    resolver: zodResolver(dealEditSchema),
    defaultValues: {
      title: deal.title,
      // U-32: строка-представление суммы (null → '', 0 → '0' — ошибка на submit, Q17).
      amount: String(deal.amount ?? ''),
      currency: deal.currency,
      companyId: deal.companyId ?? '',
      contactId: deal.contactId ?? '',
      dueDate: deal.dueDate ?? '',
      description: deal.description ?? '',
    },
  });

  // U-28: назначенный на сделку контакт ВСЕГДА в опциях (page-100/company-фильтр могут исключать),
  // подтягивается отдельным запросом useContact(deal.contactId); суффикс «(назначен)» — U-15.
  // Контакты фильтруются по выбранной компании (канон DealNewPage R17a).
  const selectedCompanyId = watch('companyId');
  const assignedContact = useContact(deal.contactId).data;
  const contactOptions = useMemo(() => {
    const base = (contactsPage?.content ?? []).filter(
      (c) => !selectedCompanyId || c.companyId === selectedCompanyId,
    );
    if (!assignedContact) return base;
    return base.some((c) => c.id === assignedContact.id)
      ? base
      : [assignedContact, ...base];
  }, [contactsPage, selectedCompanyId, assignedContact]);

  const mutation = useMutation({
    mutationFn: (values: DealEditFormValues) =>
      dealsApi.update(deal.id, {
        title: values.title,
        // U-32: схема гарантирует конечное > 0 — parseAmount даёт число в payload.
        amount: parseAmount(values.amount) as number,
        currency: values.currency as DealCurrency,
        companyId: values.companyId || null,
        contactId: values.contactId || null,
        dueDate: values.dueDate || null,
        description: values.description || null,
        // Optimistic lock (HR-D3): версия записи на момент открытия диалога.
        expectedUpdatedAt: deal.updatedAt,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['deal', deal.id] });
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
      onClose();
    },
  });

  const onSubmit: SubmitHandler<DealEditFormValues> = (values) => mutation.mutate(values);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('crm.deals.edit.title')}</DialogTitle>
      <DialogContent>
        {mutation.isError ? (
          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
            {extractApiErrorMessage(mutation.error, t('crm.deals.edit.conflict'))}
          </Alert>
        ) : null}
        <Stack
          component="form"
          id="deal-edit-form"
          spacing={2}
          sx={{ mt: 1 }}
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Название сделки"
                required
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Название сделки' }}
              />
            )}
          />
          <Stack direction="row" spacing={2}>
            <Controller
              name="amount"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  // U-32: нормализация на лету (020000 → 20000, 00.5 → 0.5).
                  onChange={(e) => field.onChange(normalizeAmountInput(e.target.value))}
                  label="Сумма"
                  required
                  fullWidth
                  type="number"
                  placeholder="0,00"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  inputProps={{ 'aria-label': 'Сумма', min: 0 }}
                />
              )}
            />
            {catalogQuery.isError ? (
              <Alert severity="error">{CURRENCIES_LOAD_ERROR}</Alert>
            ) : currencies.length > 0 ? (
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    required
                    fullWidth
                    label="Валюта"
                    inputProps={{ 'aria-label': 'Валюта' }}
                  >
                    {currencies.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.code}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            ) : null}
          </Stack>
          <Controller
            name="companyId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Компания"
                inputProps={{ 'aria-label': 'Компания' }}
              >
                <MenuItem value="">—</MenuItem>
                {[...(companyMap ?? [])].map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="contactId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Контакт"
                inputProps={{ 'aria-label': 'Контакт' }}
              >
                <MenuItem value="">—</MenuItem>
                {contactOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.id === deal.contactId
                      ? `${contactDisplayName(c)} (${t('crm.deals.edit.assignedContactSuffix')})`
                      : contactDisplayName(c)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <DatePickerField
            name="dueDate"
            control={control}
            label="Плановая дата закрытия"
            minDate="today"
            aria-label="Плановая дата закрытия"
          />
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Описание"
                fullWidth
                multiline
                minRows={3}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Описание' }}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button type="submit" form="deal-edit-form" variant="contained" disabled={mutation.isPending}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
